/**
 * Zukunftsklima im Hauptthread: Auftragssteuerung an den Zukunfts-Worker,
 * Ergebnisse je Station bzw. Ort (nur für die laufende Sitzung) und die
 * Ableitung der Richtwerte am Gebäude.
 *
 * Richtwert = Gebäudereferenz aus ERA5-Land über dieselbe Referenzperiode wie
 * die Quelle (CH2025 1991–2020, Weltbank 1995–2014) + Änderung der Quelle.
 */
import { create } from 'zustand';
import type { AnalysisConfig, Building, MetricKey, Placement, ReferenceSummary } from '../core/domain/types';
import type { ResultEntry } from '../core/worker/protocol';
import { summarizeReference } from '../core/calc/engine';
import { chooseStation, type StationChoice } from '../core/future/ch2025';
import { CCKP_INDICATORS } from '../core/future/cckp';
import { guidanceValue, thresholdsKey, type FutureIndicator, type FutureResult, type FutureRow, type FutureThresholds, type Spread } from '../core/future/types';
import type { FromFuture, ToFuture } from '../core/future/future.worker';
import { roughlyInSwitzerland } from '../geo/lv95';

export interface FutureEntry {
  status: 'laedt' | 'fertig' | 'fehler';
  done: number;
  total: number;
  result?: FutureResult;
  error?: string;
}

interface FutureState {
  entries: Record<string, FutureEntry>;
  /** Weltquelle für Schweizer Gebäude zusätzlich zeigen */
  showWorld: boolean;
  setShowWorld: (v: boolean) => void;
}

export const useFuture = create<FutureState>((set) => ({
  entries: {},
  showWorld: false,
  setShowWorld: (v) => set({ showWorld: v }),
}));

let fw: Worker | null = null;
let seq = 1;
const owners = new Map<number, string>();

function worker(): Worker {
  if (fw) return fw;
  fw = new Worker(new URL('../core/future/future.worker.ts', import.meta.url), { type: 'module', name: 'klimaraum-zukunft' });
  fw.onmessage = (ev: MessageEvent<FromFuture>) => {
    const m = ev.data;
    const key = owners.get(m.reqId);
    if (!key) return;
    const prev = useFuture.getState().entries[key];
    let next: FutureEntry;
    if (m.type === 'progress') next = { ...prev, status: 'laedt', done: m.done, total: m.total };
    else if (m.type === 'result') {
      owners.delete(m.reqId);
      next = { status: 'fertig', done: prev?.total ?? 1, total: prev?.total ?? 1, result: m.result };
    } else {
      owners.delete(m.reqId);
      next = { status: 'fehler', done: prev?.done ?? 0, total: prev?.total ?? 0, error: m.message };
    }
    useFuture.setState((s) => ({ entries: { ...s.entries, [key]: next } }));
  };
  fw.onerror = (ev) => {
    for (const [id, key] of owners) {
      owners.delete(id);
      useFuture.setState((s) => ({ entries: { ...s.entries, [key]: { status: 'fehler', done: 0, total: 0, error: `Zukunfts-Worker: ${ev.message}` } } }));
    }
  };
  return fw;
}

export function thresholdsOf(cfg: AnalysisConfig): FutureThresholds {
  return { hddBase: cfg.hddBase, cddBase: cfg.cddBase, hotDayThreshold: cfg.hotDayThreshold, heatPeriodMinDays: cfg.heatPeriodMinDays };
}

export function isSwiss(b: Building): boolean {
  return b.country === 'CH' || (!b.country && roughlyInSwitzerland(b.lat, b.lon));
}

export function buildingElevation(b: Building, p?: Placement): number | null {
  return b.elevation?.value ?? p?.targetElevation ?? null;
}

export function stationFor(b: Building, p?: Placement): StationChoice | null {
  const h = buildingElevation(b, p);
  return h === null ? null : chooseStation(b.lat, b.lon, h);
}

export const chKey = (stationId: string, t: FutureThresholds) => `ch2025|${stationId}|${thresholdsKey(t)}`;
export const worldKey = (b: Building) => `cckp|${b.lat.toFixed(3)}|${b.lon.toFixed(3)}`;

export type FutureMsg = ToFuture extends infer T ? (T extends unknown ? Omit<T, 'reqId'> : never) : never;

/** Startet einen Abruf, sofern für den Schlüssel nicht schon einer läuft oder fertig ist. */
export function startFuture(key: string, msg: FutureMsg, force = false) {
  start(key, msg, force);
}

function start(key: string, msg: FutureMsg, force = false) {
  const cur = useFuture.getState().entries[key];
  if (!force && cur && cur.status !== 'fehler') return;
  const reqId = seq++;
  owners.set(reqId, key);
  useFuture.setState((s) => ({ entries: { ...s.entries, [key]: { status: 'laedt', done: 0, total: 0 } } }));
  worker().postMessage({ ...msg, reqId } as ToFuture);
}

export function ensureCh2025(b: Building, p: Placement | undefined, cfg: AnalysisConfig, force = false): string | null {
  const c = stationFor(b, p);
  if (!c) return null;
  const t = thresholdsOf(cfg);
  const key = chKey(c.station.id, t);
  start(
    key,
    {
      kind: 'ch2025',
      apiBase: '/api',
      thresholds: t,
      location: {
        kind: 'station',
        id: c.station.id,
        name: `${c.station.name} (${c.station.abbr})`,
        lat: c.station.lat,
        lon: c.station.lon,
        elevation: c.station.h,
        distanceKm: c.distanceKm,
        elevationDiffM: c.elevationDiffM,
        representative: c.representative,
      },
    },
    force,
  );
  return key;
}

export function ensureCckp(b: Building, force = false): string {
  const key = worldKey(b);
  start(key, { kind: 'cckp', lat: b.lat, lon: b.lon }, force);
  return key;
}

/** Ergebnis mit gebäudebezogener Lage (Stationsdistanz hängt vom Gebäude ab). */
export function withBuildingLocation(r: FutureResult, c: StationChoice | null): FutureResult {
  if (r.source !== 'ch2025' || !c || c.station.id !== r.location.id) return r;
  return { ...r, location: { ...r.location, distanceKm: c.distanceKm, elevationDiffM: c.elevationDiffM, representative: c.representative } };
}

// ------------------------------------------------------------------ Richtwerte
export const INDICATOR_METRIC: Record<FutureIndicator, MetricKey> = {
  hdd: 'hdd',
  cdd: 'cdd',
  hotDays: 'hotDays',
  veryHotDays: 'veryHotDays',
  tropicalNights: 'tropicalNights',
  heatEvents: 'heatEvents',
  heatDays: 'heatDays',
  txx: 'txx',
  tMean: 'tMean',
};

export function sourceRefPeriod(r: FutureResult): [number, number] {
  return r.source === 'ch2025' ? [1991, 2020] : [1995, 2014];
}

export function buildingReference(result: ResultEntry | undefined, r: FutureResult): ReferenceSummary | null {
  if (!result) return null;
  const [a, b] = sourceRefPeriod(r);
  return summarizeReference(result.years, a, b);
}

/**
 * Richtwert nur, wenn die Gebäudereferenz dieselbe Definition hat wie die
 * Änderung. Bei der Weltquelle mit festen Schwellen gilt das nur, wenn die
 * eingestellten Schwellen passen.
 */
export function guidanceAllowed(r: FutureResult, ind: FutureIndicator, cfg: AnalysisConfig): { ok: boolean; reason?: string } {
  if (r.source === 'ch2025') return { ok: true };
  switch (ind) {
    case 'hdd':
      return Math.abs(cfg.hddBase - 18.3) <= 0.5 ? { ok: true } : { ok: false, reason: `Quelle Basis 18.3 °C, eingestellt ${cfg.hddBase} °C` };
    case 'cdd':
      return cfg.cddBase === 22 ? { ok: true } : { ok: false, reason: `Quelle Basis 22 °C, eingestellt ${cfg.cddBase} °C` };
    case 'hotDays':
      return cfg.hotDayThreshold === 30 ? { ok: true } : { ok: false, reason: `Quelle Schwelle 30 °C, eingestellt ${cfg.hotDayThreshold} °C` };
    default:
      return { ok: true };
  }
}

export interface GuidanceCell {
  delta: Spread | null;
  value: number | null;
  range: [number, number] | null;
}

export function guidance(row: FutureRow, level: string, buildingRef: number | undefined, allowed: boolean): GuidanceCell {
  const d = row.deltas[level] ?? null;
  if (!d || buildingRef === undefined || !allowed) return { delta: d, value: null, range: null };
  return {
    delta: d,
    value: guidanceValue(row.indicator, buildingRef, d.p50),
    range: [guidanceValue(row.indicator, buildingRef, d.p10), guidanceValue(row.indicator, buildingRef, d.p90)],
  };
}

export function worldDiffers(ind: FutureIndicator): string | undefined {
  return CCKP_INDICATORS.find((x) => x.indicator === ind)?.differs;
}

if (typeof window !== 'undefined') {
  (window as unknown as { __klimaraumFuture?: unknown }).__klimaraumFuture = { useFuture };
}

/** Geladene Zukunftsergebnisse eines Gebäudes (CH2025 zuerst), gebäudebezogen angepasst. */
export function futureResultsFor(b: Building, p: Placement | undefined, cfg: AnalysisConfig, entries: Record<string, FutureEntry>): FutureResult[] {
  const out: FutureResult[] = [];
  if (isSwiss(b)) {
    const c = stationFor(b, p);
    const e = c ? entries[chKey(c.station.id, thresholdsOf(cfg))] : undefined;
    if (e?.result) out.push(withBuildingLocation(e.result, c));
  }
  const w = entries[worldKey(b)];
  if (w?.result) out.push(w.result);
  return out;
}
